import type {
  AdminCompanyRoleView,
  AdminCreateCompanyRoleRequest,
  AdminUpdateCompanyRoleRequest,
  PermissionDefinitionView,
  UpdatePermissionDefinitionRequest,
} from '@safetyhub/shared';
import { getErrorMessage, isValidErrorCode } from '@safetyhub/shared';
import api, { extractApiError } from './api';

export type {
  AdminCompanyRoleView,
  AdminCreateCompanyRoleRequest,
  AdminUpdateCompanyRoleRequest,
  LegalPositionKey,
  PermissionDefinitionView,
  UpdatePermissionDefinitionRequest,
} from '@safetyhub/shared';

/**
 * admin 조직 역할·권한 API 클라이언트 (줄기 company-rbac P3 — 「조직 역할·권한」 페이지 `/company-roles`가 쓴다).
 *
 * base `/admin/rbac`. 계약 정본은 shared `AdminCompanyRoleView` JSDoc + 서버 `admin-rbac.controller.ts`.
 * ⚠️ `/roles`(`adminApi.getRoles`)는 **슈퍼관리자** 역할이고 여기는 **조직 시스템 역할**이다 — 축이 다르다.
 */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: null | { code: string; message: string };
}

const BASE = '/admin/rbac';

export const COMPANY_RBAC_QUERY_KEYS = {
  permissions: ['admin', 'company-rbac', 'permissions'] as const,
  roles: ['admin', 'company-rbac', 'roles'] as const,
};

export const companyRbacApi = {
  async listPermissions(): Promise<PermissionDefinitionView[]> {
    const res = await api.get<ApiResponse<PermissionDefinitionView[]>>(`${BASE}/permissions`);
    return res.data.data;
  },

  async updatePermission(key: string, body: UpdatePermissionDefinitionRequest): Promise<PermissionDefinitionView> {
    const res = await api.patch<ApiResponse<PermissionDefinitionView>>(
      `${BASE}/permissions/${encodeURIComponent(key)}`,
      body,
    );
    return res.data.data;
  },

  async listRoles(): Promise<AdminCompanyRoleView[]> {
    const res = await api.get<ApiResponse<AdminCompanyRoleView[]>>(`${BASE}/roles`);
    return res.data.data;
  },

  async createRole(body: AdminCreateCompanyRoleRequest): Promise<AdminCompanyRoleView> {
    const res = await api.post<ApiResponse<AdminCompanyRoleView>>(`${BASE}/roles`, body);
    return res.data.data;
  },

  async updateRole(key: string, body: AdminUpdateCompanyRoleRequest): Promise<AdminCompanyRoleView> {
    const res = await api.patch<ApiResponse<AdminCompanyRoleView>>(`${BASE}/roles/${encodeURIComponent(key)}`, body);
    return res.data.data;
  },

  async archiveRole(key: string): Promise<AdminCompanyRoleView> {
    const res = await api.post<ApiResponse<AdminCompanyRoleView>>(`${BASE}/roles/${encodeURIComponent(key)}/archive`, {});
    return res.data.data;
  },

  async restoreRole(key: string): Promise<AdminCompanyRoleView> {
    const res = await api.post<ApiResponse<AdminCompanyRoleView>>(`${BASE}/roles/${encodeURIComponent(key)}/restore`, {});
    return res.data.data;
  },
};

/**
 * 에러 → 사용자 문구. 서버가 자리 전용 문구를 실어 보낸다(archive의 `ROLE_HAS_HOLDERS`는 조직 수·인원 수, owner 보관은
 * 「소유자 역할은 보관할 수 없습니다」 — shared 기본 문구는 조직 화면 어휘(「삭제」)라 여기서는 서버 문구가 우선).
 * 순서: 응답 봉투 `error.message` → shared 코드 기본 문구 → 호출부 fallback(네트워크·알 수 없는 예외).
 */
export function companyRbacErrorMessage(error: unknown, fallback: string): string {
  const { code, message } = extractApiError(error);
  if (code === 'NETWORK_ERROR' || code === 'UNKNOWN_ERROR') return fallback;
  if (message) return message;
  if (isValidErrorCode(code)) return getErrorMessage(code);
  return fallback;
}
